import { addDays } from 'date-fns';
import {
    CaseClosingBlocker,
    CaseClosingCheckDto,
    CloseCaseXrChoice,
    CloseMatterRequestDto,
    ConfidentialityDecision,
} from 'types/caseClosing';
import { MinStringOption, NamedEntityMinDto } from 'types/other';
import { TaskForm } from 'types/tasks';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';

export const XR_TASK_DEADLINE_DAYS = 14;

/** The firm's "Internal task" value of the TASK_TYPE classifier; a firm without it gets no task type. */
export const INTERNAL_TASK_TYPE_CODE = 'INTERNAL_TASK';

export interface CloseCaseFormValues {
    closedDate: string | null;
    xrChoice: CloseCaseXrChoice;
}

/** The new-task form's values the "Remind me to write XR" choice fills in, carried in the route state. */
export interface CloseCaseTaskPrefill {
    name: string;
    description: string;
    taskTypeCode: string;
    assignedToEmployees: NamedEntityMinDto[];
    deadlineDate: string;
}

export interface CloseCaseTaskPrefillState {
    taskPrefill?: CloseCaseTaskPrefill;
}

export function isCaseClosingBlocked(check: CaseClosingCheckDto): boolean {
    return check.conditions.some((condition) => !condition.isMet);
}

export function isCloseCaseButtonDisabled(check: CaseClosingCheckDto | undefined, isCheckFetching: boolean): boolean {
    return !check || isCheckFetching || !check.isClosable;
}

export function isConditionMet(check: CaseClosingCheckDto, blocker: CaseClosingBlocker): boolean {
    return check.conditions.find((condition) => condition.blocker === blocker)?.isMet ?? true;
}

export function buildCloseMatterRequest(
    isConfidential: boolean,
    closedDate: string,
    decision: ConfidentialityDecision | null,
    reason: string,
    xrChoice: CloseCaseXrChoice
): CloseMatterRequestDto {
    const trimmedReason = reason.trim();
    return {
        closedDate,
        confidentialityDecision: isConfidential ? decision : null,
        confidentialReason:
            isConfidential && decision === ConfidentialityDecision.KEEP && trimmedReason ? trimmedReason : null,
        xrChoice,
    };
}

/** The deadline counts from the click, not from the closing date, which may lie in the past. */
export function buildXrTaskPrefill(
    caseFullName: string,
    closingUser: NamedEntityMinDto,
    clickedAt: Date
): CloseCaseTaskPrefill {
    return {
        name: t('case-closing-modal.xr-task.name'),
        description: t('case-closing-modal.xr-task.description', { case: caseFullName }),
        taskTypeCode: INTERNAL_TASK_TYPE_CODE,
        assignedToEmployees: [{ id: closingUser.id, name: closingUser.name }],
        deadlineDate: formatDateStringInput(addDays(clickedAt, XR_TASK_DEADLINE_DAYS)),
    };
}

export function applyTaskPrefill(
    form: TaskForm,
    prefill: CloseCaseTaskPrefill,
    taskTypeOptions: MinStringOption[]
): TaskForm {
    const taskType = taskTypeOptions.find((option) => option.value === prefill.taskTypeCode);
    return {
        ...form,
        name: prefill.name,
        description: prefill.description,
        taskTypes: taskType ? [{ label: taskType.label, value: taskType.value }] : [],
        assignedToEmployees: prefill.assignedToEmployees,
        hasDeadline: true,
        deadlineDate: prefill.deadlineDate,
    };
}
